import React, { useState, useEffect, useRef } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useLang } from "@/i18n";
import { User, Menu, X, ChevronDown, UserPlus, LogIn } from "lucide-react";
import { api } from "@/lib/api";
import { useGuestAuth } from "@/context/GuestAuthContext";

function LanguageSwitcher({ solid }) {
  const { lang, setLang, languages } = useLang();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const current = languages.find(l => l.code === lang) || languages[0];

  useEffect(() => {
    const onClick = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  return (
    <div className="relative" ref={ref}>
      <button onClick={() => setOpen(o => !o)} aria-label="Sprache wählen" aria-expanded={open} aria-haspopup="listbox"
        className={`flex items-center gap-1.5 text-sm font-medium px-3 py-2 rounded-full transition-colors hover:text-[var(--terra)] ${solid ? "text-[var(--ink)]" : "text-white"}`}>
        <img src={`https://flagcdn.com/24x18/${current.flag}.png`} alt="" width="20" height="15" className="rounded-[2px] shadow-sm shrink-0" />
        {current.code.toUpperCase()} <ChevronDown size={12} className={`transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className="absolute right-0 mt-2 w-48 bg-white rounded-2xl shadow-[0_16px_48px_rgba(19,22,15,0.16)] border border-[var(--warm)] py-2 overflow-hidden z-50">
          {languages.map(l => (
            <button key={l.code} onClick={() => { setLang(l.code); setOpen(false); }}
              className={`w-full text-left px-4 py-2.5 text-sm flex items-center gap-2.5 transition-colors hover:bg-[var(--warm)] ${l.code === lang ? "text-[var(--terra)] font-semibold" : "text-[var(--ink)]"}`}>
              <img src={`https://flagcdn.com/24x18/${l.flag}.png`} alt="" width="20" height="15" className="rounded-[2px] shadow-sm shrink-0" />
              <span className="flex-1">{l.label}</span>
              <span className="text-xs text-[var(--muted)]">{l.code.toUpperCase()}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export const Navbar = () => {
  const { t, lang } = useLang();
  const { guest } = useGuestAuth();
  const [scrolled, setScrolled] = useState(false);
  const [siteSettings, setSiteSettings] = useState({});
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const onHome = location.pathname === "/";

  useEffect(() => {
    const fn = () => setScrolled(window.scrollY > 60);
    fn(); window.addEventListener("scroll", fn);
    return () => window.removeEventListener("scroll", fn);
  }, []);

  useEffect(() => { setMobileOpen(false); }, [location.pathname]);
  useEffect(() => { api.get("/settings").then(r => setSiteSettings(r.data)).catch(() => {}); }, []);

  const solid = scrolled || !onHome || mobileOpen;

  return (
    <header style={{ top: "var(--promo-h, 0px)" }} className={`fixed left-0 right-0 z-50 transition-all duration-500 ${solid ? "bg-[var(--cream)] border-b border-[var(--warm)]" : "bg-transparent"}`}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-20 flex items-center justify-between">
        {/* Logo */}
        <Link to="/" className="flex flex-col leading-none">
          {siteSettings.logo_image
            ? <img src={siteSettings.logo_image} alt="Logo" className="h-9 object-contain" />
            : <>
                <span className={`font-display text-2xl font-medium tracking-tight transition-colors duration-300 ${solid ? "text-[var(--ink)]" : "text-white"}`}>
                  {siteSettings.brand_name || "ATLAS"}
                </span>
                <span className={`text-[10px] tracking-[0.3em] lowercase transition-colors duration-300 ${solid ? "text-[var(--terra)]" : "text-white/60"}`}>
                  {siteSettings.brand_sub || "apartment"}
                </span>
              </>
          }
        </Link>

        {/* Desktop nav */}
        <nav className="hidden md:flex items-center gap-9">
          {[{ to: "/", label: t("nav_home") }, { to: "/apartments", label: t("nav_apartments") }, { to: "/blog", label: "Blog" }].map(l => (
            <Link key={l.to} to={l.to}
              className={`text-[15px] font-medium transition-colors duration-300 hover:text-[var(--terra)] ${solid ? "text-[var(--ink)]" : "text-white"}`}>
              {l.label}
            </Link>
          ))}
        </nav>

        {/* Right */}
        <div className="flex items-center gap-1.5 sm:gap-2">
          <LanguageSwitcher solid={solid} />
          {guest ? (
            <button onClick={() => navigate("/mein-konto")}
              className={`hidden sm:flex items-center gap-1.5 text-sm font-medium px-3 py-2 transition-colors hover:text-[var(--terra)] ${solid ? "text-[var(--ink)]" : "text-white"}`}>
              <User size={13} /> {guest.name.split(" ")[0]}
            </button>
          ) : (
            <button onClick={() => navigate("/anmelden")}
              className={`hidden sm:flex items-center gap-1.5 text-sm font-medium px-3 py-2 transition-colors hover:text-[var(--terra)] ${solid ? "text-[var(--ink)]" : "text-white"}`}>
              <User size={13} /> {t("nav_signin")}
            </button>
          )}

          {/* Mobil: sofort sichtbarer Registrieren/Konto-Button, nicht erst im Menü versteckt */}
          {guest ? (
            <button onClick={() => navigate("/mein-konto")}
              className={`sm:hidden flex items-center justify-center w-9 h-9 rounded-full border transition-colors ${solid ? "border-[var(--ink)]/15 text-[var(--ink)]" : "border-white/30 text-white"}`}
              aria-label={t("nav_myaccount")}>
              <User size={15} />
            </button>
          ) : (
            <button onClick={() => navigate("/registrieren")}
              className="sm:hidden flex items-center gap-1.5 px-3.5 py-2 rounded-full bg-[var(--terra)] hover:bg-[var(--terra-dark)] text-white text-xs font-semibold transition-colors">
              <UserPlus size={13} /> {t("guest_register")}
            </button>
          )}

          <button onClick={() => navigate("/apartments")}
            className="hidden sm:flex items-center px-6 py-2.5 bg-[var(--sage)] hover:bg-[var(--sage-dark)] text-white text-sm font-semibold rounded-full transition-colors">
            {t("nav_book")}
          </button>
          <button onClick={() => setMobileOpen(!mobileOpen)} aria-label={mobileOpen ? "Menü schließen" : "Menü öffnen"} aria-expanded={mobileOpen}
            className={`sm:hidden p-2 transition-colors ${solid ? "text-[var(--ink)]" : "text-white"}`}>
            {mobileOpen ? <X size={20} /> : <Menu size={20} />}
          </button>
        </div>
      </div>

      {/* Mobile menu */}
      {mobileOpen && (
        <div className="sm:hidden bg-[var(--cream)] border-t border-[var(--warm)] px-4 py-6 space-y-4">
          {/* Konto-Bereich zuerst, gut sichtbar */}
          {guest ? (
            <button onClick={() => navigate("/mein-konto")}
              className="w-full flex items-center gap-3 px-4 py-3.5 rounded-2xl bg-[var(--warm)] text-[var(--ink)] font-semibold text-sm">
              <User size={16} /> {guest.name}
            </button>
          ) : (
            <div className="grid grid-cols-2 gap-3">
              <button onClick={() => navigate("/registrieren")}
                className="flex items-center justify-center gap-2 py-3.5 rounded-2xl bg-[var(--terra)] hover:bg-[var(--terra-dark)] text-white text-sm font-semibold">
                <UserPlus size={15} /> {t("guest_register")}
              </button>
              <button onClick={() => navigate("/anmelden")}
                className="flex items-center justify-center gap-2 py-3.5 rounded-2xl border border-[var(--ink)]/15 text-[var(--ink)] text-sm font-semibold">
                <LogIn size={15} /> {t("login")}
              </button>
            </div>
          )}

          <div className="space-y-1">
            {[{ to: "/", label: t("nav_home") }, { to: "/apartments", label: t("nav_apartments") }, { to: "/blog", label: "Blog" }].map(l => (
              <Link key={l.to} to={l.to}
                className="block py-3 text-[15px] font-medium text-[var(--ink)] border-b border-[var(--warm)]">
                {l.label}
              </Link>
            ))}
          </div>

          <button onClick={() => navigate("/apartments")}
            className="w-full btn-primary justify-center">
            {t("nav_book")}
          </button>
        </div>
      )}
    </header>
  );
};
